/** Page texts outside the pixel canvas (the canvas itself is English): controls caption, touch-button labels. FR / EN, chosen from the device unless the player picked one. */
export type Lang = "fr" | "en";
export const TEXTS: Record<Lang, Record<string, string>> = {
  fr: {
    controls:
      "Z/Espace = accélérer · X/↓ = freiner · ↑ ou C = jet · ←/→ = direction · Entrée = valider · M = son · N = musique · R = rétroviseur<br>Manette : stick/croix = direction · A ou RT = gaz · B ou LT = frein · X/Y/RB = jet · Start = valider · Select = quitter",
    snd: "SON",
    donate: "&hearts; Don volontaire (PayPal)",
    up: "Haut",
    left: "Gauche",
    right: "Droite",
    down: "Bas",
    langTitle: "Switch to English",
    iabMsg: "Tu joues dans le navigateur d'une appli (Facebook, Messenger...). Certaines fonctions marchent mal et ta licence n'est pas partagée avec ton vrai navigateur. Ouvre le jeu dans Chrome ou Safari.",
    iabIos: "Touche le menu ··· (ou l'icône boussole) puis « Ouvrir dans Safari ».",
    iabChrome: "Ouvrir dans Chrome",
    iabCopy: "Copier le lien",
    iabCopied: "Lien copié !",
    iabClose: "Fermer",
  },
  en: {
    controls:
      "Z/Space = accelerate · X/↓ = brake · ↑ or C = jet · ←/→ = steer · Enter = confirm · M = sound · N = music · R = mirror<br>Gamepad: stick/d-pad = steer · A or RT = gas · B or LT = brake · X/Y/RB = jet · Start = confirm · Select = quit",
    donate: "&hearts; Support the game (PayPal)",
    snd: "SOUND",
    up: "Up",
    left: "Left",
    right: "Right",
    down: "Down",
    langTitle: "Passer en français",
    iabMsg: "You are playing inside an app's browser (Facebook, Messenger...). Some features work badly and your licence is not shared with your real browser. Open the game in Chrome or Safari.",
    iabIos: "Tap the ··· menu (or the compass icon) then \"Open in Safari\".",
    iabChrome: "Open in Chrome",
    iabCopy: "Copy link",
    iabCopied: "Link copied!",
    iabClose: "Close",
  },
};

/** The saved choice wins; otherwise French for French-speaking devices, English for everything else. */
export function detectLang(saved: string | null | undefined, languages: readonly string[] | undefined): Lang {
  if (saved === "fr" || saved === "en") return saved;
  const first = (languages && languages[0]) || "en";
  return /^fr\b/i.test(first) ? "fr" : "en";
}

let lang: Lang = "en";
export const currentLang = () => lang;

export function applyLang(l: Lang) {
  lang = l;
  const t = TEXTS[l];
  document.documentElement.lang = l;
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((el) => {
    const v = t[el.dataset.i18n ?? ""];
    if (v !== undefined) el.innerHTML = v; // trusted strings from TEXTS above
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((el) => {
    const v = t[el.dataset.i18nAria ?? ""];
    if (v !== undefined) el.setAttribute("aria-label", v);
  });
  // the toggle shows the language you would switch TO
  document.querySelectorAll<HTMLElement>("[data-lang-toggle]").forEach((el) => {
    el.textContent = l === "fr" ? "EN" : "FR";
    el.title = t.langTitle;
  });
}

/** Chooses the language (page texts and canvas texts) and remembers it. */
export function setLang(l: Lang) {
  try {
    localStorage.setItem("f1lang", l);
  } catch {
    /* storage unavailable */
  }
  applyLang(l);
}

export function initLang() {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem("f1lang");
  } catch {
    /* storage unavailable */
  }
  applyLang(detectLang(saved, navigator.languages ?? [navigator.language]));
  document.querySelectorAll<HTMLElement>("[data-lang-toggle]").forEach((el) =>
    el.addEventListener("click", () => {
      const next: Lang = lang === "fr" ? "en" : "fr";
      try {
        localStorage.setItem("f1lang", next);
      } catch {
        /* storage unavailable */
      }
      applyLang(next);
      el.blur(); // Enter / Space belong to the game, not to this button
    })
  );
}
